import React from 'react';
import { IconEye, IconBuilding, IconServer, IconDiagram3, IconLightbulb, IconFileEarmarkCode } from './OnboardingIcons';

const OnboardPreview = ({ data, onSubmit, isSubmitting }) => {
  const renderServiceEndpoints = () => {
    const endpoints = data.infrastructure_endpoints || {};
    const endpointEntries = Object.entries(endpoints).filter(([_, url]) => url && url.trim() !== '');
    
    if (endpointEntries.length === 0) return <span className="empty">No endpoints configured</span>;
    
    return (
      <div className="preview-endpoints">
        {endpointEntries.map(([type, url], index) => (
          <div key={index} className="preview-endpoint">
            <span className="endpoint-type">{type}:</span>
            <a
              className="endpoint-url"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {url}
            </a>
          </div>
        ))}
      </div>
    );
  };

  const renderAssetPatterns = () => {
    const pattern = data.asset_id_pattern || {};
    const patterns = [];
    
    if (pattern.asset_id_starts_with) patterns.push(`Starts: ${pattern.asset_id_starts_with}`);
    if (pattern.asset_id_contains) patterns.push(`Contains: ${pattern.asset_id_contains}`);
    if (pattern.asset_id_ends_with) patterns.push(`Ends: ${pattern.asset_id_ends_with}`);
    if (pattern.global_asset_id) patterns.push(`Example: ${pattern.global_asset_id}`);
    
    const serialPattern = pattern.serial_number_pattern || {};
    if (serialPattern.serial_number_format) patterns.push(`Format: ${serialPattern.serial_number_format}`);
    if (serialPattern.serial_number_starts_with) patterns.push(`SN Starts: ${serialPattern.serial_number_starts_with}`);
    if (serialPattern.serial_number_contains) patterns.push(`SN Contains: ${serialPattern.serial_number_contains}`);
    if (serialPattern.serial_number_ends_with) patterns.push(`SN Ends: ${serialPattern.serial_number_ends_with}`);
    
    if (patterns.length === 0) return <span className="empty">No patterns configured</span>;
    
    return (
      <div className="preview-patterns">
        {patterns.map((pattern, index) => (
          <span key={index} className="pattern-badge">{pattern}</span>
        ))}
      </div>
    );
  };

  const renderKeywords = (keywords, type) => {
    const cleanKeywords = (keywords || []).map((item) => (item || '').trim()).filter(Boolean);
    if (cleanKeywords.length === 0) return <span className="empty">No {type} configured</span>;
    
    return (
      <div className="preview-keywords">
        {cleanKeywords.map((keyword, index) => (
          <span key={index} className={`keyword-badge ${type}`}>
            {type === 'submodel' && <IconFileEarmarkCode aria-hidden />}
            {keyword}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h3>
          <IconEye aria-hidden />
          Preview & Submit
        </h3>
        <p>Check your details before publishing them to the data space.</p>
      </div>

      <div className="preview-container">
        {/* Data Provider Information Preview */}
        <div className="preview-section">
          <h4>
            <IconBuilding aria-hidden />
            Data Provider Information
          </h4>
          <div className="preview-content">
            <div className="preview-item">
              <label>Company Name:</label>
              <span>{data.company_data?.company_name || <span className="empty">Not specified</span>}</span>
            </div>
            <div className="preview-item">
              <label>Description:</label>
              <span>{data.company_data?.company_description || <span className="empty">Not specified</span>}</span>
            </div>
            <div className="preview-item">
              <label>Domain:</label>
              <span>{data.domain || <span className="empty">Not specified</span>}</span>
            </div>
            <div className="preview-item">
              <label>Website:</label>
              <span>{data.company_data?.homepage_url || <span className="empty">Not specified</span>}</span>
            </div>
          </div>
        </div>

        {/* Service Endpoints Preview */}
        <div className="preview-section">
          <h4>
            <IconServer aria-hidden />
            Service Endpoints
          </h4>
          <div className="preview-content">
            {renderServiceEndpoints()}
          </div>
        </div>

        {/* Asset Patterns Preview */}
        <div className="preview-section">
          <h4>
            <IconDiagram3 aria-hidden />
            Asset Patterns
          </h4>
          <div className="preview-content">
            {renderAssetPatterns()}
          </div>
        </div>

        {/* Capabilities Preview */}
        <div className="preview-section">
          <h4>
            <IconLightbulb aria-hidden />
            Capabilities & Keywords
          </h4>
          <div className="preview-content">
            <div className="preview-item">
              <label>Description:</label>
              <span>{data.capabilities?.capability_description || <span className="empty">Not specified</span>}</span>
            </div>
            <div className="preview-item">
              <label>Language:</label>
              <span>{data.capabilities?.language || <span className="empty">Not specified</span>}</span>
            </div>
            <div className="preview-item">
              <label>Asset Kind:</label>
              <span>{data.capabilities?.asset_kind || <span className="empty">Not specified</span>}</span>
            </div>
            <div className="preview-keyword-group preview-keyword-group--keywords">
              <label>Keywords:</label>
              {renderKeywords(data.capabilities?.keywords, 'keyword')}
            </div>
            <div className="preview-keyword-group preview-keyword-group--capabilities">
              <label>Capability Types:</label>
              {renderKeywords(data.capabilities?.capability_types, 'capability')}
            </div>
            <div className="preview-keyword-group preview-keyword-group--submodels">
              <label>Submodels Supported:</label>
              {renderKeywords(data.capabilities?.submodels_supported, 'submodel')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardPreview;
